import React, { useEffect, useMemo, useRef, useState } from 'react';
import { strategyGrowth } from '../../utils/publicShareMath';

/**
 * Public results page: each strategy's activity, a month at a time (as on
 * Results). Strategies with trades list their trades (they come with the
 * page; on a link without backtested results they start at the live-since
 * date). Daily-balance strategies have no trades, so they list each day's
 * change and value at the page's starting capital, from the same curve the
 * chart draws. `view` narrows both the way the chart is: 'live' from the
 * live-since date, 'backtested' before it, 'full' everything.
 *
 * `strategies` are the ones picked at the top of the page. The list follows
 * that choice: turning a strategy on shows it here, and one turned off is
 * neither shown nor offered.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const money = (n, { sign = false } = {}) => {
  if (n == null) return '—';
  const s = `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (!sign) return n < 0 ? `−${s}` : s;
  return `${n < 0 ? '−' : '+'}${s}`;
};
const price = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const shortDate = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return `${m}/${d}/${y}`;
};
const monthKey = (iso) => String(iso).slice(0, 7);
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

export function tradesInView(trades, liveSince, view) {
  if (!liveSince || view === 'full') return trades || [];
  if (view === 'backtested') return (trades || []).filter((t) => t.date < liveSince);
  return (trades || []).filter((t) => t.date >= liveSince);
}

/** A daily-balance strategy's days: [{ date, change, changePct, value }] on `capital`, oldest first. */
export function dailyRows(strategy, view, capital) {
  const values = strategyGrowth(strategy, view, null, capital) || [];
  const out = [];
  for (let i = 1; i < values.length; i += 1) {
    const prev = values[i - 1].value;
    const change = values[i].value - prev;
    out.push({ date: values[i].date, change, changePct: prev ? (change / prev) * 100 : null, value: values[i].value });
  }
  return out;
}

const hasTrades = (s) => Array.isArray(s?.trades) && s.trades.length > 0;

export default function ShareTradeList({ strategies, view, capital = 100000 }) {
  const listed = useMemo(() => (strategies || []).filter((s) => hasTrades(s) || (s.points || []).length >= 2), [strategies]);
  const [name, setName] = useState(listed[0]?.name || '');
  // Follow the picks at the top: switch to a strategy just turned on.
  const seen = useRef(listed.map((s) => s.name));
  useEffect(() => {
    const names = listed.map((s) => s.name);
    const added = names.filter((n) => !seen.current.includes(n));
    seen.current = names;
    if (added.length) setName(added[added.length - 1]);
  }, [listed]);
  const strategy = listed.find((s) => s.name === name) || listed[0] || null;
  const daily = strategy && !hasTrades(strategy);
  const trades = useMemo(() => {
    if (!strategy) return [];
    if (!daily) return tradesInView(strategy.trades, strategy.liveSince, view);
    return dailyRows(strategy, view, capital);
  }, [strategy, daily, view, capital]);
  const months = useMemo(() => [...new Set(trades.map((t) => monthKey(t.date)))].sort(), [trades]);
  const [month, setMonth] = useState(null);
  // Latest month by default, and whenever the strategy or view leaves the chosen month empty.
  useEffect(() => {
    if (!months.length) { setMonth(null); return; }
    if (!month || !months.includes(month)) setMonth(months[months.length - 1]);
  }, [months, month]);

  if (!listed.length) return null;
  const rows = trades.filter((t) => monthKey(t.date) === month).sort((a, b) => (a.date < b.date ? 1 : -1));
  const at = months.indexOf(month);
  const hasSide = rows.some((t) => t.side);
  const hasSymbol = rows.some((t) => t.symbol);
  const net = rows.reduce((sum, t) => sum + (daily ? t.change : t.pnl), 0);
  const wins = rows.filter((t) => (daily ? t.change : t.pnl) > 0).length;
  const unit = daily ? ['day', 'days'] : ['trade', 'trades'];

  return (
    <section className="psp-card psp-trades" aria-label={daily ? 'Daily results' : 'List of trades'}>
      <span className="psp-eyebrow">Activity</span>
      <div className="psp-trades-head">
        <h2>{daily ? 'Daily results' : 'List of trades'}</h2>
        <div className="psp-trades-controls">
          {listed.length > 1 ? (
            <select value={strategy?.name || ''} onChange={(e) => setName(e.target.value)} aria-label="Strategy">
              {listed.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
          ) : <span className="psp-trades-name">{strategy?.name}</span>}
          <div className="psp-trades-month">
            <button type="button" onClick={() => setMonth(months[at - 1])} disabled={at <= 0} aria-label="Previous month">‹</button>
            <select value={month || ''} onChange={(e) => setMonth(e.target.value)} aria-label="Month">
              {[...months].reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            <button type="button" onClick={() => setMonth(months[at + 1])} disabled={at < 0 || at >= months.length - 1} aria-label="Next month">›</button>
          </div>
        </div>
      </div>

      {month && daily ? (
        <>
          <p className="psp-muted psp-small psp-trades-sum">
            {rows.length} {rows.length === 1 ? unit[0] : unit[1]} in {monthLabel(month)} · {wins} up · net <b className={net < 0 ? 'is-neg' : 'is-pos'}>{money(net, { sign: true })}</b> on {money(capital)}
          </p>
          <div className="psp-trades-wrap">
            <table className="psp-trades-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Day’s change</th>
                  <th className="num">Change %</th>
                  <th className="num">Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.date}>
                    <td>{shortDate(r.date)}</td>
                    <td className={`num ${r.change < 0 ? 'is-neg' : r.change > 0 ? 'is-pos' : ''}`}>{money(r.change, { sign: r.change !== 0 })}</td>
                    <td className={`num ${r.change < 0 ? 'is-neg' : r.change > 0 ? 'is-pos' : ''}`}>{r.changePct == null ? '—' : `${r.changePct < 0 ? '−' : r.changePct > 0 ? '+' : ''}${Math.abs(r.changePct).toFixed(2)}%`}</td>
                    <td className="num">{money(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="psp-muted psp-small">This strategy reports one account balance a day rather than individual trades. Each day’s change is on the {money(capital)} starting capital above.</p>
        </>
      ) : month ? (
        <>
          <p className="psp-muted psp-small psp-trades-sum">
            {rows.length} {rows.length === 1 ? unit[0] : unit[1]} in {monthLabel(month)} · {wins} won · net <b className={net < 0 ? 'is-neg' : 'is-pos'}>{money(net, { sign: true })}</b>
          </p>
          <div className="psp-trades-wrap">
            <table className="psp-trades-table">
              <thead>
                <tr>
                  <th>Trade date</th>
                  {hasSymbol ? <th>Symbol</th> : null}
                  {hasSide ? <th>Side</th> : null}
                  <th>Status</th>
                  <th className="num">Volume</th>
                  <th className="num">Entry price</th>
                  <th className="num">Exit price</th>
                  <th className="num">Net P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`${t.date}-${i}`}>
                    <td>{shortDate(t.date)}</td>
                    {hasSymbol ? <td>{t.symbol || '—'}</td> : null}
                    {hasSide ? <td>{t.side ? <span className={`psp-tag is-${t.side.toLowerCase()}`}>{t.side}</span> : '—'}</td> : null}
                    <td><span className={`psp-tag is-${t.status.toLowerCase()}`}>{t.status}</span></td>
                    <td className="num">{t.volume == null ? '—' : t.volume.toLocaleString()}</td>
                    <td className="num">{price(t.entry)}</td>
                    <td className="num">{price(t.exit)}</td>
                    <td className={`num ${t.pnl < 0 ? 'is-neg' : t.pnl > 0 ? 'is-pos' : ''}`}>{money(t.pnl, { sign: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="psp-muted psp-small">Trades as recorded, at their own contract counts (the chart above uses the size shown under What’s included).</p>
        </>
      ) : <p className="psp-muted">No trades in this view.</p>}
    </section>
  );
}
